import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import DebouncedText from "components/DebouncedText";
import {
  CalcCaseProperty,
  CustomPropertyCategory,
  CustomPropertyType,
  DeletePropSelectOptionsDocument,
  MigrateCustomPropertyTypeDocument,
} from "generated/graphql";
import { distinct } from "helpers/coll";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { FC, useState } from "react";
import { upsertCalcProperty } from "redux/slices/lensEditor";
import SelectOptions from "components/SelectOptions/SelectOptions";
import { useFormik } from "formik";
import { Edit } from "@mui/icons-material";
import PropertySelectOptionDialog from "routes/lens-editor/profile-builder/PropertySelectOptionDialog";
import { useSnackbar } from "notistack";
import { runGqlMutation } from "gqlClient";

interface Props {
  open: boolean;
  close: () => void;
  property?: CalcCaseProperty;
  lensId: number;
}
const CalcPropertyDialog: FC<Props> = ({ open, close, property, lensId }) => {
  const [showMigrateSelectOptionDialog, setMigrateSelectOptionDialog] = useState(false);
  const snackpatch = useAppDispatchWithSnackbar();
  const { enqueueSnackbar } = useSnackbar();

  const formik = useFormik({
    initialValues: {
      key: property?.key || "",
      displayName: property?.displayName || "",
      dataType: property?.dataType,
      selectOptions: property?.selectOptions || [],
      isCollectVisible: property?.isCollectVisible ?? false,
      description: property?.description || undefined,
    },
    validate: (values) => {
      const errors: Partial<typeof values> = {};

      if (values.key.length === 0) {
        errors.key = "Required";
      }
      if (values.displayName.length === 0) {
        errors.displayName = "Required";
      }
      if (values.dataType === undefined) {
        errors.dataType = CustomPropertyType.Text;
      }
      return errors;
    },
    onSubmit: (values) => {
      if (values.dataType !== undefined) {
        const currentProperty = {
          ...values,
          dataType: values.dataType,
          lensId,
        };
        const migratePropOrDeleteSelectOptionFunc = async () => {
          const oldDataType = property?.dataType;
          const newDataType = formik.values.dataType;
          const deletedSelectOptions = (property?.selectOptions || []).filter(
            (o) => !formik.values.selectOptions.includes(o)
          );
          if (oldDataType !== undefined && newDataType !== undefined && oldDataType !== newDataType) {
            return runGqlMutation(MigrateCustomPropertyTypeDocument, {
              lensId,
              key: formik.values.key,
              dataType: newDataType,
              propCategory: CustomPropertyCategory.CaseCalcProperty,
            });
          } else if (deletedSelectOptions.length > 0) {
            return runGqlMutation(DeletePropSelectOptionsDocument, {
              lensId,
              key: values.key,
              selectOptionsToDelete: deletedSelectOptions,
              propCategory: CustomPropertyCategory.CaseCalcProperty,
            });
          }
        };
        migratePropOrDeleteSelectOptionFunc()
          .then(() =>
            snackpatch(upsertCalcProperty(currentProperty), {
              successMessage: "Calc property saved",
              errorMessage: "Failed to save calc property",
            }).then(() => {
              close();
            })
          )
          .catch(() =>
            enqueueSnackbar(`Failed to update calc property`, {
              variant: "error",
            })
          );
      }
    },
  });

  return (
    <Dialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>{property ? "Update" : "New"} Calc</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            pt: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}>
          <FormControl fullWidth required>
            <TextField
              name="displayName"
              value={formik.values.displayName}
              error={!!formik.errors.displayName && formik.touched.displayName}
              onBlur={formik.handleBlur}
              helperText={formik.touched.displayName && formik.errors.displayName}
              label="Display Name"
              onChange={formik.handleChange}
            />
          </FormControl>
          <FormControl fullWidth required>
            <DebouncedText
              value={formik.values.key}
              textFieldProps={{
                name: "key",
                disabled: !!property,
                helperText: formik.touched.key && formik.errors.key ? formik.errors.key : "No spaces",
                onBlur: formik.handleBlur,
                error: !!formik.errors.key && formik.touched.key,
              }}
              onChange={(value) => formik.setFieldValue("key", value)}
              formatter={(text) => text.toLowerCase().replace(/\s/g, "")}
              delayMs={1}
              label="Key"
            />
          </FormControl>

          <FormControl fullWidth>
            <TextField
              name="description"
              value={formik.values.description}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label="Description"
            />
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Data Type</InputLabel>
            <Select
              disabled={!!property && property.dataType === CustomPropertyType.Text}
              label="Data Type"
              name="dataType"
              onBlur={formik.handleBlur}
              value={formik.values.dataType}
              onChange={(e) => {
                const dt = e.target.value as CustomPropertyType;
                formik.setFieldValue("dataType", dt);
                if (dt !== CustomPropertyType.Select) {
                  formik.setFieldValue("selectOptions", []);
                }
                if (dt === CustomPropertyType.Json) {
                  formik.setFieldValue("isCaseHomeVisible", false);
                  formik.setFieldValue("isCollectVisible", false);
                }
              }}>
              <MenuItem value={CustomPropertyType.Text}>Text</MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Date
                }
                value={CustomPropertyType.Date}>
                Date
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Number
                }
                value={CustomPropertyType.Number}>
                Number
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Select
                }
                value={CustomPropertyType.Select}>
                Select
              </MenuItem>
              <MenuItem
                disabled={
                  !!property &&
                  property.dataType !== CustomPropertyType.Text &&
                  property.dataType !== CustomPropertyType.Json
                }
                value={CustomPropertyType.Json}>
                JSON
              </MenuItem>
            </Select>
          </FormControl>
          {formik.values.dataType === CustomPropertyType.Select && (
            <div className="flex mx-4">
              {showMigrateSelectOptionDialog && (
                <PropertySelectOptionDialog
                  onClose={() => setMigrateSelectOptionDialog(false)}
                  availableSelectOptions={formik.values.selectOptions}
                  onSuccess={(oldValue: string, newValue: string) => {
                    setMigrateSelectOptionDialog(false);
                    const newSelectOptions = formik.values.selectOptions.map((o) => (o === oldValue ? newValue : o));
                    formik.setFieldValue("selectOptions", newSelectOptions);
                  }}
                  propertyType={CustomPropertyCategory.CaseCalcProperty}
                  propertyKey={formik.values.key}
                />
              )}
              {property !== undefined && (
                <div>
                  <Tooltip title="Rename Option" className="mt-2">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setMigrateSelectOptionDialog(true);
                      }}>
                      <Edit />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
              <SelectOptions
                options={formik.values.selectOptions}
                setOptions={(options) => {
                  const distinctValues = distinct(options);
                  formik.setFieldValue("selectOptions", distinctValues);
                }}
              />
            </div>
          )}

          <FormControl fullWidth disabled={formik.values.dataType === CustomPropertyType.Json}>
            <FormLabel>Is Collect Visible</FormLabel>
            <RadioGroup
              row
              name="isCollectVisible"
              value={formik.values.isCollectVisible}
              onChange={(e) => {
                formik.setFieldValue("isCollectVisible", e.target.value === "true");
              }}
              onBlur={formik.handleBlur}>
              <FormControlLabel value={false} control={<Radio />} label="No" />
              <FormControlLabel value={true} control={<Radio />} label="Yes" />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={close}>
          Cancel
        </Button>
        <Button
          disabled={!formik.isValid}
          color="primary"
          sx={{ ml: 2 }}
          onClick={formik.submitForm}
          variant="contained">
          {!!property && property?.dataType !== formik.values.dataType ? "Migrate and " : ""}Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CalcPropertyDialog;
